import { DrillthroughTableInjectableRow } from 'common/components/table/drillthrough-table/drillthrough-table.types';
import { AdvancedReportTableRow } from '../advanced-reports-table.types';
import {
  createCallCell,
  createClicksCell,
  createCostCell,
  createCpaCell,
  createCpsCell,
  createFieldCell,
  createImpressionsCell,
  createOpportunityCell,
  createOpportunityValueCell,
  createRevenueCell,
  createRoasCell,
  createRoiCell,
  createSaleCell,
  createWebCell,
  fieldColumnId
} from '../advanced-reports-column-defs';
import { MetricTypes } from 'common/constants/metric-types';
import {
  createInsightsFieldCell,
  createOccurrencesCell
} from 'features/insights/conversion-influence/conversion-influence-column-defs';
import { InsightsTableRow } from 'features/insights/types/insights-table.types';

export const createDrillthroughTableInjectableRow = (
  row: AdvancedReportTableRow,
  currencyCode: string,
  columnIds: string[],
  displayComparisonAttributionModelData: boolean
): DrillthroughTableInjectableRow => {
  const visibleCells = [
    {
      columnId: fieldColumnId,
      cellId: `${row.id}_${fieldColumnId}`,
      element: createFieldCell(
        row.field,
        row.id,
        row.hasChildren,
        row.shouldBlurField
      ),
      isBlurred: row.shouldBlurField
    },
    {
      columnId: MetricTypes.IMPRESSIONS,
      cellId: `${row.id}_${MetricTypes.IMPRESSIONS}`,
      element: createImpressionsCell(
        row.impressions,
        displayComparisonAttributionModelData
      ),
      isBlurred: false
    },
    {
      columnId: MetricTypes.CLICKS,
      cellId: `${row.id}_${MetricTypes.CLICKS}`,
      element: createClicksCell(
        row.clicks,
        displayComparisonAttributionModelData
      ),
      isBlurred: false
    },
    {
      columnId: MetricTypes.INBOUND_CALL,
      cellId: `${row.id}_${MetricTypes.INBOUND_CALL}`,
      element: createCallCell(row.call, displayComparisonAttributionModelData),
      isBlurred: false
    },
    {
      columnId: MetricTypes.WEB,
      cellId: `${row.id}_${MetricTypes.WEB}`,
      element: createWebCell(row.web, displayComparisonAttributionModelData),
      isBlurred: false
    },
    {
      columnId: MetricTypes.OPPORTUNITIES,
      cellId: `${row.id}_${MetricTypes.OPPORTUNITIES}`,
      element: createOpportunityCell(
        row.opportunity,
        displayComparisonAttributionModelData
      ),
      isBlurred: false
    },
    {
      columnId: MetricTypes.OPPORTUNITY_VALUES,
      cellId: `${row.id}_${MetricTypes.OPPORTUNITY_VALUES}`,
      element: createOpportunityValueCell(
        row.opportunityValue,
        currencyCode,
        displayComparisonAttributionModelData
      ),
      isBlurred: false
    },
    {
      columnId: MetricTypes.REVENUES,
      cellId: `${row.id}_${MetricTypes.REVENUES}`,
      element: createSaleCell(row.sales, displayComparisonAttributionModelData),
      isBlurred: false
    },
    {
      columnId: MetricTypes.REVENUE_VALUES,
      cellId: `${row.id}_${MetricTypes.REVENUE_VALUES}`,
      element: createRevenueCell(
        row.revenue,
        currencyCode,
        displayComparisonAttributionModelData
      ),
      isBlurred: false
    },
    {
      columnId: MetricTypes.COST,
      cellId: `${row.id}_${MetricTypes.COST}`,
      element: createCostCell(
        row.cost,
        currencyCode,
        displayComparisonAttributionModelData
      ),
      isBlurred: false
    },
    {
      columnId: MetricTypes.CPA,
      cellId: `${row.id}_${MetricTypes.CPA}`,
      element: createCpaCell(
        row.cpa,
        currencyCode,
        displayComparisonAttributionModelData
      ),
      isBlurred: false
    },
    {
      columnId: MetricTypes.CPS,
      cellId: `${row.id}_${MetricTypes.CPS}`,
      element: createCpsCell(
        row.cps,
        currencyCode,
        displayComparisonAttributionModelData
      ),
      isBlurred: false
    },
    {
      columnId: MetricTypes.ROI,
      cellId: `${row.id}_${MetricTypes.ROI}`,
      element: createRoiCell(
        row.roi,
        currencyCode,
        displayComparisonAttributionModelData
      ),
      isBlurred: false
    },
    {
      columnId: MetricTypes.ROAS,
      cellId: `${row.id}_${MetricTypes.ROAS}`,
      element: createRoasCell(row.roas, displayComparisonAttributionModelData),
      isBlurred: false
    }
  ].filter((cell) => columnIds.includes(cell.columnId));

  const drillthroughRow: DrillthroughTableInjectableRow = {
    rowId: row.id,
    parentId: row.parentId,
    hasChildren: row.hasChildren,
    depth: row.depth,
    cells: visibleCells
  };

  return drillthroughRow;
};

export const createDrillthroughInsightsTableInjectableRow = (
  row: InsightsTableRow,
  columnIds: string[]
): DrillthroughTableInjectableRow => {
  const visibleCells = [
    {
      columnId: fieldColumnId,
      cellId: `${row.id}_${fieldColumnId}`,
      element: createInsightsFieldCell(row.field, row.id, row.hasChildren),
      isBlurred: false
    },
    {
      columnId: 'Occurrences',
      cellId: `${row.id}_occurrences`,
      element: createOccurrencesCell(row.occurrences),
      isBlurred: false
    }
  ].filter((cell) => columnIds.includes(cell.columnId));

  const drillthroughRow: DrillthroughTableInjectableRow = {
    rowId: row.id,
    parentId: row.parentId,
    hasChildren: row.hasChildren,
    depth: row.depth,
    cells: visibleCells
  };

  return drillthroughRow;
};
